// Edycja zapisanego filtra klienta. Dwa równoprawne wejścia na te same dane:
//
//   * „Formularz” — odwzorowanie panelu filtrów z aplikacji klienta,
//   * „JSON” — surowy `UserFilters.filter`, na wypadek kryterium, którego formularz nie
//     pokazuje, i do wklejenia filtra w całości.
//
// Źródłem prawdy jest obiekt; zakładka JSON serializuje go przy wejściu, a wraca do niego
// wyłącznie przez jawne „Zastosuj JSON” — inaczej każdy znak wpisany w textarei walczyłby
// z reserializacją i skakał kursorem.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Input,
  Space,
  Spin,
  Switch,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { FiltersCell } from "../../components/FiltersPreview";
import { SavedFilterForm } from "../../components/SavedFilterForm";
import {
  NotificationPreferenceCard,
  type NotificationDraft,
} from "../../components/NotificationPreferenceCard";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import { isEmptyFiltersDisplay } from "../../types/filters";
import type { AdminFilterDetail, FilterDictionaries, SavedFilter } from "../../types/filterAdmin";
import { EMPTY_SAVED_FILTER } from "../../types/filterAdmin";

const { Title, Paragraph, Text } = Typography;

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pl-PL");
}

/** Wiersz z bazy w kształt, na którym pracuje formularz. Braki uzupełniamy defaultami. */
function toSavedFilter(raw: SavedFilter | null): SavedFilter {
  if (!raw || typeof raw !== "object") return { ...EMPTY_SAVED_FILTER };
  const queries = Array.isArray(raw.queries)
    ? raw.queries.map(String).map((q) => q.trim()).filter((q) => q.length > 0 && q !== "*")
    : // Stary filtr ma same `search_text` — front trzyma w nim frazy sklejone nową linią.
      String(raw.search_text ?? "")
        .split("\n")
        .map((q) => q.trim())
        .filter((q) => q.length > 0 && q !== "*");

  return {
    search_text: queries.join("\n"),
    queries,
    excluded_phrases: Array.isArray(raw.excluded_phrases) ? raw.excluded_phrases.map(String) : [],
    filters: raw.filters && typeof raw.filters === "object" ? { ...raw.filters } : {},
    sort: typeof raw.sort === "string" && raw.sort ? raw.sort : "_score",
    direction: typeof raw.direction === "string" && raw.direction ? raw.direction : "desc",
    limit: typeof raw.limit === "number" ? raw.limit : Number(raw.limit) || 25,
  };
}

export const FilterEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const [detail, setDetail] = useState<AdminFilterDetail | null>(null);
  const [dictionaries, setDictionaries] = useState<FilterDictionaries | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingNotification, setSavingNotification] = useState(false);

  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [filter, setFilter] = useState<SavedFilter>({ ...EMPTY_SAVED_FILTER });
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("form");

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await adminFetch<{ data: AdminFilterDetail }>(
        `filters/${encodeURIComponent(id)}`,
      );
      const data = response.data;
      setDetail(data);
      setName(data.name ?? "");
      setIsPublic(Boolean(data.public));
      setFilter(toSavedFilter(data.filter));
    } catch (error) {
      notification.error({ message: "Nie udało się wczytać filtra", description: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [id, notification]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    adminFetch<FilterDictionaries>("filter-dictionaries")
      .then(setDictionaries)
      .catch(() => {
        // Bez słowników formularz nadal działa — selecty branż i województw stają się puste,
        // a zakładka JSON jest w pełni sprawna. Nie blokujemy edycji.
        notification.warning({
          message: "Nie udało się pobrać słowników",
          description: "Listy branż i województw będą puste. Kryteria można ustawić w zakładce JSON.",
        });
      });
  }, [notification]);

  const handleTabChange = (key: string) => {
    if (key === "json") {
      setJsonText(JSON.stringify(filter, null, 2));
      setJsonError(null);
    }
    setActiveTab(key);
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText) as SavedFilter;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setJsonError("Filtr musi być obiektem JSON.");
        return;
      }
      setFilter(toSavedFilter(parsed));
      setJsonError(null);
      setActiveTab("form");
      notification.success({ message: "JSON wczytany do formularza" });
    } catch (error) {
      setJsonError(errorMessage(error));
    }
  };

  const handleSave = async () => {
    if (!id) return;
    if (!name.trim()) {
      notification.error({
        message: "Pusta nazwa",
        description: "Klient rozpoznaje filtry po nazwie — nadaj ją przed zapisem.",
      });
      return;
    }
    // Zakładka JSON jest źródłem prawdy tylko po „Zastosuj JSON”. Zapis z otwartej zakładki
    // bez zastosowania zapisałby stan sprzed edycji, więc mówimy o tym wprost.
    if (activeTab === "json" && jsonText !== JSON.stringify(filter, null, 2)) {
      notification.warning({
        message: "Niezastosowany JSON",
        description: "Kliknij „Zastosuj JSON”, zanim zapiszesz — inaczej zapiszą się poprzednie kryteria.",
      });
      return;
    }

    setSaving(true);
    try {
      await adminFetch(`filters/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ name: name.trim(), public: isPublic, filter }),
      });
      notification.success({
        message: "Filtr zapisany",
        description: "Klient zobaczy zmienione kryteria od razu.",
      });
      await load();
    } catch (error) {
      notification.error({ message: "Nie udało się zapisać filtra", description: errorMessage(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotification = async (draft: NotificationDraft) => {
    if (!id) return;
    setSavingNotification(true);
    try {
      await adminFetch(`filters/${encodeURIComponent(id)}/notification`, {
        method: "PUT",
        body: JSON.stringify(draft),
      });
      notification.success({ message: "Ustawienia powiadomień zapisane" });
      await load();
    } catch (error) {
      notification.error({
        message: "Nie udało się zapisać ustawień powiadomień",
        description: errorMessage(error),
      });
    } finally {
      setSavingNotification(false);
    }
  };

  const handleDeleteNotification = async () => {
    if (!id) return;
    setSavingNotification(true);
    try {
      await adminFetch(`filters/${encodeURIComponent(id)}/notification`, { method: "DELETE" });
      notification.success({
        message: "Powiadomienia filtra wyłączone",
        description: "Filtr zostaje zapisany, ale przestaje cokolwiek wysyłać.",
      });
      await load();
    } catch (error) {
      notification.error({
        message: "Nie udało się wyłączyć powiadomień",
        description: errorMessage(error),
      });
    } finally {
      setSavingNotification(false);
    }
  };

  const emptyFilter = useMemo(
    () => detail?.filters_display != null && isEmptyFiltersDisplay(detail.filters_display),
    [detail],
  );

  if (loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  if (!detail) {
    return (
      <Card>
        <Alert type="error" showIcon message="Nie znaleziono filtra" />
        <Button style={{ marginTop: 16 }} onClick={() => navigate("/filters")}>
          Wróć do listy
        </Button>
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <Title level={4} style={{ marginBottom: 0 }}>
            Edycja filtra klienta
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Zapis nadpisuje filtr, który klient ma w aplikacji. Zmieniamy tylko nazwę,
            kryteria i flagę publiczności — właściciela filtra nigdy.
          </Paragraph>

          <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered>
            <Descriptions.Item label="Właściciel">{detail.created_by}</Descriptions.Item>
            <Descriptions.Item label="Utworzono">{formatDate(detail.created_at)}</Descriptions.Item>
            <Descriptions.Item label="Ostatnia zmiana">{formatDate(detail.modified_at)}</Descriptions.Item>
          </Descriptions>

          <div>
            <Text strong>Nazwa filtra</Text>
            <Input
              style={{ marginTop: 4 }}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Nazwa widoczna dla klienta"
            />
          </div>

          <Space>
            <Switch checked={isPublic} onChange={setIsPublic} />
            <Text>Filtr publiczny (widoczny dla całej organizacji klienta)</Text>
          </Space>

          <div>
            <Text strong>Zapisana zawartość: </Text>
            <FiltersCell filters={detail.filters_display} />
          </div>

          {emptyFilter && (
            <Alert
              type="warning"
              showIcon
              message="Filtr nie zawęża niczego"
              description="Bez ani jednej frazy i ani jednego kryterium filtr pasuje do wszystkich ogłoszeń — a przy włączonych powiadomieniach wysyła klientowi cały indeks."
            />
          )}
          {detail.filters_display && detail.filters_display.branches.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Stara taksonomia „branches”"
              description="Filtr używa pola branches[] zamiast cpv_code[] (sprzed migracji F5). Działa, ale filtruje po innej taksonomii niż kody CPV."
            />
          )}
        </Space>
      </Card>

      <Card
        title="Kryteria"
        extra={
          <Space>
            {detail.public && <Tag color="gold">publiczny</Tag>}
            <Button type="primary" loading={saving} onClick={handleSave}>
              Zapisz filtr
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "form",
              label: "Formularz",
              children: (
                <SavedFilterForm value={filter} onChange={setFilter} dictionaries={dictionaries} />
              ),
            },
            {
              key: "json",
              label: "JSON",
              children: (
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Surowa zawartość kolumny <code>UserFilters.filter</code>. Backend i tak
                    znormalizuje kształt przy zapisie: przełączniki jako <code>true</code>,
                    budżety jako liczby, daty jako tekst ISO — takiej postaci wymaga
                    wyszukiwarka, żeby nie zwinąć filtra do samego zapytania tekstowego.
                  </Paragraph>
                  <Input.TextArea
                    rows={22}
                    value={jsonText}
                    onChange={(event) => setJsonText(event.target.value)}
                    style={{ fontFamily: "monospace" }}
                  />
                  {jsonError && <Alert type="error" showIcon message="Niepoprawny JSON" description={jsonError} />}
                  <Space>
                    <Button onClick={applyJson}>Zastosuj JSON</Button>
                    <Button onClick={() => setJsonText(JSON.stringify(filter, null, 2))}>
                      Przywróć z formularza
                    </Button>
                  </Space>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <NotificationPreferenceCard
        subscription={detail.notification}
        delivery={detail.delivery}
        dictionaries={dictionaries}
        saving={savingNotification}
        onSave={handleSaveNotification}
        onDelete={handleDeleteNotification}
      />

      <Button onClick={() => navigate("/filters")}>Wróć do listy filtrów</Button>
    </Space>
  );
};
